import { Button, Card, Col, Divider, Flex, Form, FormProps, Input, Modal, Row } from 'antd';
import FileInput from 'components/fileInput/fileInput';
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import NotificationService from 'core/services/notification';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { checkPhoneValidation } from 'utils/validators/phone';
import { createCompanyProfile, getCompanySectors, getCompanyTypes } from '../services/companyProfileService';
import SelectElement from 'components/selectElement/selectElement';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import { useWatch } from 'antd/es/form/Form';
import { isRTL } from 'core/services/language';
import { CompanyGenericInfo, CompanyType } from 'core/models/company';

export default function CompanyRegisterForm() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [companyForm] = Form.useForm();

	const nominalValue = useWatch('nominal_value_of_the_share', companyForm) ?? 0;

	const [companyTypes, setCompanyTypes] = useState<CompanyType[]>([]);
	const [companySectors, setCompanySectors] = useState<CompanyGenericInfo[]>([]);

	const [isLoading, setIsLoading] = useState(false);
	const [isCompanyDataLoading, setIsCompanyDataLoading] = useState(false);

	const [isCompanyTypeModalOpen, setIsCompanyTypeModalOpen] = useState(true);
	const [selectedCompanyTypeId, setSelectedCompanyTypeId] = useState<string>();

	type FieldType = {
		'company_name[ar]': string;
		'company_name[en]': string;
		company_type_id: string;
		company_sector_id: string;
		capital: string;
		nominal_value_of_the_share: string;
		commercial_registration_number: string;
		un_commercial_registration_number: string;
		tax_number: string;
		email: string;
		company_address: string;
		company_logo: any;
		company_stamp: any;
	};

	useEffect(() => {
		getCompaniesData();
	}, []);

	const getCompaniesData = async () => {
		try {
			setIsCompanyDataLoading(true);
			const [companyTypesResponse, companySectorsResponse] = await Promise.all([getCompanyTypes(), getCompanySectors()]);
			setCompanyTypes(companyTypesResponse.company_types);
			setCompanySectors(companySectorsResponse.company_types);
			setIsCompanyDataLoading(false);
		} catch (error) {}
	};

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const validateParValue = (_: any, value: string) => {
		const parsedValue = parseFloat(value);

		if (value === undefined || value === null) return Promise.resolve();

		if (parsedValue === 0) return Promise.reject(t('COMMON.FIELD_REQUIRED'));

		if (parsedValue == 0.5) return Promise.resolve();

		if (parsedValue >= 1 && Number.isInteger(parsedValue)) return Promise.resolve();

		return Promise.reject(t('REGISTER.PAR_VALUE_VALIDATION'));
	};

	const validateAuthorizedCapital = (_: any, value: string) => {
		const parsedValue = parseFloat(value);
		const capital = parseFloat(companyForm.getFieldValue('capital'));

		if (!isNaN(parsedValue) && !isNaN(capital) && parsedValue < capital) {
			return Promise.reject(t('REGISTER.AUTHORIZED_CAPITAL_VALIDATION'));
		}

		return Promise.resolve();
	};

	const validateCapital = (_: any, value: string) => {
		const parsedValue = parseFloat(value);
		const authorizedCapital = parseFloat(companyForm.getFieldValue('authorized_capital'));

		if (!isNaN(parsedValue) && !isNaN(authorizedCapital) && parsedValue > authorizedCapital) {
			return Promise.reject(t('REGISTER.AUTHORIZED_CAPITAL_VALIDATION'));
		}

		return Promise.resolve();
	};

	const onAuthorizedCapitalChange = () => {
		companyForm.validateFields(['capital']).catch(() => {});
	};

	const onCapitalChange = () => {
		companyForm.validateFields(['authorized_capital']).catch(() => {});
	};

	const onConfirmCompanyType = () => {
		setIsCompanyTypeModalOpen(false);
	};

	const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const createCompanyPayload = new FormData();
			createCompanyPayload.append('_method', 'put');

			Object.entries(values).forEach(([key, value]) => {
				createCompanyPayload.append(key, value);
			});

			await createCompanyProfile(createCompanyPayload);

			window.scrollTo({ top: 0 });

			setTimeout(() => {
				setIsLoading(false);
				navigate('/register/plans');
			}, 400);
		} catch (error: any) {
			setIsLoading(false);
			mapResponseErrors(error, companyForm);
			const errorMessage = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), errorMessage);
		}
	};

	return (
		<>
			<Modal
				open={isCompanyTypeModalOpen}
				title={t('REGISTER.SELECT_COMPANY_TYPE')}
				closable={false}
				maskClosable={false}
				keyboard={false}
				centered
				footer={
					<Button type="primary" disabled={!selectedCompanyTypeId} onClick={onConfirmCompanyType}>
						{t('COMMON.CONTINUE')}
					</Button>
				}>
				<p className="text-light">{t('REGISTER.SELECT_COMPANY_TYPE_DESC')}</p>
				<SelectElement
					fullWidth
					options={companyTypes.map((item) => ({
						label: isRTL() ? item?.translated_name?.ar : item?.translated_name?.en,
						value: item.id.toString(),
					}))}
					isLoading={isCompanyDataLoading}
					label={t('REGISTER.COMPANY_TYPE')}
					onChange={(value) => setSelectedCompanyTypeId(value as string)}
				/>
			</Modal>

			<motion.h2
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.3,
				}}
				key="title">
				{t('REGISTER.CREATE_COMPANY_PROFILE')}
			</motion.h2>

			<motion.p
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.5,
				}}
				key="subTitle"
				className="text-light">
				{t('REGISTER.FILL_COMPANY_DATA')}
			</motion.p>

			{!isCompanyTypeModalOpen && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{
						duration: 0.5,
					}}
					key="form"
					style={{ textAlign: 'start' }}
					className="container">
					<Card>
						<Form
							name="companyForm"
							form={companyForm}
							initialValues={{ company_type_id: selectedCompanyTypeId }}
							onFinish={onFinish}
							autoComplete="off"
							scrollToFirstError>
							<Row>
								<Col span={24} className="p-2">
									<h4>{t('REGISTER.COMPANY_INF')}</h4>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="company_name[ar]"
										type="text"
										label={t('REGISTER.COMPANY_AR_NAME')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
									/>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="company_name[en]"
										type="text"
										label={t('REGISTER.COMPANY_EN_NAME')}
										onChange={(value) =>
											companyForm.setFieldsValue({
												'company_name[en]': value
													.toLowerCase()
													.split(' ')
													.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
													.join(' '),
											})
										}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
									/>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<Flex vertical>
										<Form.Item name="company_type_id" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
											<SelectElement
												name="company_type_id"
												options={companyTypes.map((item) => ({
													label: isRTL() ? item?.translated_name?.ar : item?.translated_name?.en,
													value: item.id.toString(),
												}))}
												isLoading={isCompanyDataLoading}
												label={t('REGISTER.COMPANY_TYPE')}
												onChange={(value) => {
													companyForm.setFieldsValue({ company_type_id: value });
													setSelectedCompanyTypeId(value as string);
												}}
											/>
										</Form.Item>
									</Flex>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<Flex vertical>
										<Form.Item name="company_sector_id" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
											<SelectElement
												name="company_sector_id"
												options={companySectors.map((item) => ({
													label: isRTL() ? item?.translated_name?.ar : item?.translated_name?.en,
													value: item.id.toString(),
												}))}
												isLoading={isCompanyDataLoading}
												label={t('REGISTER.COMPANY_SECTOR')}
												onChange={(value) => companyForm.setFieldsValue({ company_sector_id: value })}
											/>
										</Form.Item>
									</Flex>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="un_commercial_registration_number"
										type="number"
										label={t('REGISTER.UNIFIED_REGISTRATION_NUMBER')}
										rules={[
											{ required: true, message: t('COMMON.FIELD_REQUIRED') },
											{ len: 10, message: t('REGISTER.UNIFIED_REGISTRATION_NUMBER_LEN') },
										]}
									/>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<AnimatePresence mode="wait">
										{companyTypes.find((item) => item.id.toString() == selectedCompanyTypeId)?.legal_entity_type == 'company' && (
											<motion.div
												key="commercial_registration_number"
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												exit={{ opacity: 0 }}
												transition={{ duration: 0.2 }}>
												<FormElement
													noAutoComplete
													name="commercial_registration_number"
													type="number"
													label={t('REGISTER.COMMERCIAL_REGISTRATION_NUMBER')}
													rules={[
														{
															required: companyTypes.find((item) => item.id.toString() == selectedCompanyTypeId)?.legal_entity_type == 'company',
															message: t('COMMON.FIELD_REQUIRED'),
														},
														{ len: 10, message: t('REGISTER.COMMERCIAL_REGISTRATION_NUMBER_LEN') },
													]}
												/>
											</motion.div>
										)}
									</AnimatePresence>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="tax_number"
										type="number"
										label={t('REGISTER.VAT_REGISTRATION_NUMBER')}
										rules={[
											{ required: true, message: t('COMMON.FIELD_REQUIRED') },
											{ len: 15, message: t('REGISTER.VAT_REGISTRATION_NUMBER_LEN') },
										]}
									/>
								</Col>
								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="email"
										label={t('COMMON.EMAIL')}
										type="email"
										rules={[
											{ required: true, message: t('COMMON.FIELD_REQUIRED') },
											{ type: 'email', message: t('COMMON.EMAIL_INVALID') },
										]}
									/>
								</Col>
								<Col span={24} md={12} lg={8} className="p-2">
									<FormElement
										noAutoComplete
										name="company_address"
										type="text"
										label={t('REGISTER.NATIONAL_ADDRESS')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									/>
								</Col>

								<Divider className="my-3" />

								<Col span={24} className="p-2">
									<h4>{t('REGISTER.CAPITAL_INFORMATION')}</h4>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormNumberElement
										noAutoComplete
										name="authorized_capital"
										label={t('REGISTER.AUTHORIZED_CAPITAL')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateAuthorizedCapital }]}
										onChange={onAuthorizedCapitalChange}
									/>
								</Col>

								<Col span={24} md={12} lg={8} className="p-2">
									<FormNumberElement
										noAutoComplete
										name="capital"
										label={t('REGISTER.CAPITAL')}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateCapital }]}
										onChange={onCapitalChange}
									/>
								</Col>
								<Col span={24} md={12} lg={8} className="p-2">
									<FormNumberElement
										noAutoComplete
										name="nominal_value_of_the_share"
										label={t('REGISTER.PAR_VALUE')}
										precision={2}
										step={nominalValue >= 1 ? 1 : 0.5}
										rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateParValue }]}
									/>
								</Col>
							</Row>

							<Divider className="my-3" />

							<Row className="mt-3">
								<Col span={24} className="p-2 mb-3">
									<Form.Item name="company_logo" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
										<FileInput
											onSelect={(file) => companyForm.setFieldsValue({ company_logo: file })}
											name={t('REGISTER.COMPANY_LOGO')}
											accept="photo"
										/>
									</Form.Item>
								</Col>
								<Col span={24} className="p-2">
									<Form.Item name="company_stamp" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
										<FileInput
											onSelect={(file) => companyForm.setFieldsValue({ company_stamp: file })}
											name={t('REGISTER.COMPANY_STAMP')}
											accept="photo"
										/>
									</Form.Item>
								</Col>
							</Row>

							<Row justify="end">
								<Col className="py-4 px-2">
									<Button type="primary" ghost htmlType="submit" loading={isLoading}>
										{t('COMMON.NEXT')}
									</Button>
								</Col>
							</Row>
						</Form>
					</Card>
				</motion.div>
			)}
		</>
	);
}
